import React, { useState, useEffect } from 'react'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useFormContext } from 'react-hook-form'
import type { OnboardTabProps, CustomDocumentPayload } from './onboard.types'
import type { EmployeeOnBoard } from '@/interface/employeeOnBoard'
import {
    FileText,
    Trash2,
    Plus,
    UserCheck,
    GraduationCap,
    FolderKanban,
    CheckCircle2,
    UploadCloud,
    Eye,
    Download,
    AlertCircle,
} from 'lucide-react'
import { API_URL } from '@/api/constant'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import { ThrowError } from '@/api/callApi'
import { showToastNotification, validateFileType, ALLOWED_PHOTO_PDF_ACCEPT } from '@/utility/helper'
import CommonModal from '@/components/common/CommonModal'
import FilePreviewModal from '@/components/common/FilePreviewModal'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'


// ── doc.name (from API) → form field name (for GET restoration) ──────────────
const FIXED_DOC_NAME_TO_FIELD: Record<string, string> = {
    'Aadhar Card Front': 'aadharFront',
    'Aadhar Card Back': 'aadharBack',
    'PAN Card': 'panFront',
    'Driving License': 'drivingLicense',
    'Passport': 'passport',
    'Marriage Certificate': 'marriageCertificate',
    'Current Address Proof': 'currentAddressProof',
    'Permanent Address Proof': 'permanentAddressProof',
    'SSC Marksheet': 'sscDoc',
    'SSC': 'sscDoc',
    'HSC Marksheet': 'hscDoc',
    'HSC': 'hscDoc',
    'Graduation Marksheet': 'graduationDoc',
    'Graduation': 'graduationDoc',
    'Post Graduation Marksheet': 'postGraduationDoc',
    // 'Post Graduation': 'postGraduationDoc',
    'Masters Marksheet': 'mastersDoc',
    'Masters': 'mastersDoc',
}

// Base personal fields (always shown)
const BASE_PERSONAL_FIELDS = ['aadharFront', 'aadharBack', 'panFront', 'drivingLicense', 'passport', 'currentAddressProof', 'permanentAddressProof']
const EDUCATION_FIELDS = ['sscDoc', 'hscDoc', 'graduationDoc', 'mastersDoc']


interface CustomDocItem {
    id: string
    title: string
    file: string
    isUploading?: boolean
    keyName?: string
    /** Server document ID — present only for docs already saved via API */
    docId?: string
}



type Section = 'personal' | 'education' | 'other'
const OnboardDocuments: React.FC<OnboardTabProps> = ({
    isLoading: isParentLoading,
    handleNextStep,
    uploadFile,
    empId,
    // loggedInUserId,
}) => {
    const methods = useFormContext()
    const { formState: { errors }, setValue, watch } = methods

    // ── Check marital status to conditionally show Marriage Certificate ─────────
    const isMarried = watch('isMarried') === '1'
    // Dynamic personal fields: adds marriageCertificate when user is married
    const PERSONAL_FIELDS = isMarried
        ? [...BASE_PERSONAL_FIELDS, 'marriageCertificate']
        : BASE_PERSONAL_FIELDS

    const currentId = empId

    // ── Saved doc IDs (for delete confirmation) ──────────────────────────────────
    const [docIds, setDocIds] = useState<Record<string, string>>({})
    const [isDeleting, setIsDeleting] = useState(false)

    // ── File in-app preview modal ────────────────────────────────────────────────
    const [previewDoc, setPreviewDoc] = useState<{ url: string; title: string } | null>(null)

    // ── Delete confirmation modal ────────────────────────────────────────────────
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [docToDelete, setDocToDelete] = useState<{
        name: string      // fieldName or custom doc item id
        label: string     // display label
        docId?: string    // server docId if saved
        section?: Section // section name for custom docs
        isCustom?: boolean
    } | null>(null)

    // ── Per-section custom doc state (FULLY INDEPENDENT) ────────────────────────
    const [personalCustomDocs, setPersonalCustomDocs] = useState<CustomDocItem[]>([])
    const [educationCustomDocs, setEducationCustomDocs] = useState<CustomDocItem[]>([])
    const [otherDocs, setOtherDocs] = useState<CustomDocItem[]>([])

    // ── Accordion open sections & validation submit tracking ─────────────────
    const [openSections, setOpenSections] = useState<string[]>(['personal-docs'])
    const [hasSubmitted, setHasSubmitted] = useState(false)

    // ── Load & restore saved documents on mount ────────────────────────────
    useEffect(() => {
        if (!currentId) return
        const fetchDocs = async () => {
            try {
                const response = await EmployeeOnBoardService.getEmployeeDocuments(currentId)
                // console?.log("response", response)
                if (response?.status && response?.data) {
                    const docList: any[] = Array.isArray(response.data)
                        ? response.data
                        : response.data.documents || []

                    // docIds keyed by fieldName (e.g. 'aadharFront') for fixed docs
                    const idsMap: Record<string, string> = {}
                    const newPersonal: CustomDocItem[] = []
                    const newEducation: CustomDocItem[] = []
                    const newOther: CustomDocItem[] = []

                    docList.forEach((doc: any) => {
                        if (!doc.id) return

                        const fieldName = FIXED_DOC_NAME_TO_FIELD[doc.name]
                        if (fieldName) {
                            // ── Fixed doc: restore form field URL + store ID ──
                            idsMap[fieldName] = doc.id
                            if (doc.url) setValue(fieldName, doc.url)
                        } else {
                            // ── Custom doc: restore into the right section array ──
                            const item: CustomDocItem = {
                                id: doc.id,
                                title: doc.name || '',
                                file: doc.url || '',
                                docId: doc.id,
                                keyName: doc?.keyName

                            }
                            if (doc.type === 'PERSONAL') newPersonal.push(item)
                            else if (doc.type === 'EDUCATION') newEducation.push(item)
                            else newOther.push(item)
                        }
                    })

                    setDocIds(idsMap)
                    if (newPersonal.length) setPersonalCustomDocs(newPersonal)
                    if (newEducation.length) setEducationCustomDocs(newEducation)
                    if (newOther.length) setOtherDocs(newOther)
                }
            } catch (err) {
                console.error('Error loading documents:', err)
            }
        }
        fetchDocs()
    }, [currentId])

    // ── Fixed-field file upload ──────────────────────────────────────────────────
    const handleFileChange = (name: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (!validateFileType(file)) {
            e.target.value = ''
            return
        }

        if (uploadFile) {
            const path = await uploadFile(file)
            if (path) {
                setValue(name, path, { shouldValidate: true })
                methods.clearErrors(name)
            }
        }
    }

    // ── Download file helper ──────────────────────────────────────────────────
    const handleDownload = async (fileUrl: string, fileName?: string) => {
        try {
            const fullUrl = `${API_URL}/${fileUrl}`
            const response = await fetch(fullUrl)
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = fileName || fileUrl.split('/').pop() || 'document'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            const link = document.createElement('a')
            link.href = `${API_URL}/${fileUrl}`
            link.download = fileName || 'document'
            link.target = '_blank'
            link.click()
        }
    }

    // ── Delete confirmation action ──────────────────────────────────────────────
    const confirmDelete = async () => {
        if (!docToDelete) return
        const { name, docId, section, isCustom } = docToDelete
        setIsDeleting(true)
        try {
            if (docId) {
                const response = await EmployeeOnBoardService.deleteEmployeeDocument(docId)
                if (response?.status) {
                    showToastNotification('success', 'Document deleted successfully')
                    if (isCustom && section) {
                        getSetterForSection(section)(prev => prev.filter(item => item.id !== name))
                    } else {
                        setDocIds(prev => {
                            const updated = { ...prev }
                            delete updated[name]
                            return updated
                        })
                        setValue(name, '', { shouldValidate: true })
                    }
                }
            } else {
                if (isCustom && section) {
                    getSetterForSection(section)(prev => prev.filter(item => item.id !== name))
                } else {
                    setValue(name, '', { shouldValidate: true })
                }
                showToastNotification('success', 'Document removed successfully')
            }
            setIsDeleteModalOpen(false)
            setDocToDelete(null)
        } catch (err) {
            console.error('Error deleting document:', err)
            ThrowError(err)
        } finally {
            setIsDeleting(false)
        }
    }

    // ── Custom doc handlers (dispatch to correct section state) ─────────────────
    const getSetterForSection = (section: Section) => {
        if (section === 'personal') return setPersonalCustomDocs
        if (section === 'education') return setEducationCustomDocs
        return setOtherDocs
    }

    const handleAddCustomDoc = (section: Section) => {
        getSetterForSection(section)(prev => [
            ...prev,
            { id: Date.now().toString(), title: '', file: '' },
        ])
    }

    const handleRemoveCustomDoc = (section: Section, id: string) => {
        const allDocs = section === 'personal' ? personalCustomDocs
            : section === 'education' ? educationCustomDocs
                : otherDocs
        const doc = allDocs.find(d => d.id === id)

        if (doc?.file || doc?.docId) {
            // File IS uploaded (or server doc exists) → Open confirmation modal!
            setDocToDelete({
                name: id,
                label: doc.title.trim() || 'Custom Document',
                docId: doc.docId,
                section,
                isCustom: true,
            })
            setIsDeleteModalOpen(true)
        } else {
            // Empty row (no file uploaded) → Remove directly from state without modal
            getSetterForSection(section)(prev => prev.filter(item => item.id !== id))
        }
    }

    const handleCustomDocTitleChange = (section: Section, id: string, title: string) => {
        getSetterForSection(section)(prev =>
            prev.map(item => item.id === id ? { ...item, title } : item)
        )
    }

    const handleCustomDocFileChange =
        (section: Section, id: string) =>
            async (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0]
                if (!file || !uploadFile) return

                if (!validateFileType(file)) {
                    e.target.value = ''
                    return
                }

                const setter = getSetterForSection(section)
                setter(prev => prev.map(item => item.id === id ? { ...item, isUploading: true } : item))
                const path = await uploadFile(file)
                setter(prev =>
                    prev.map(item =>
                        item.id === id ? { ...item, file: path || '', isUploading: false } : item
                    )
                )
            }

    const personalUploaded = PERSONAL_FIELDS.filter(f => !!watch(f)).length
    const educationUploaded = EDUCATION_FIELDS.filter(f => !!watch(f)).length

    // ── Build custom doc payload for API ──────────────────────────────────────
    const buildCustomDocPayload = (): CustomDocumentPayload[] => {
        // Send ALL valid custom docs across all sections (both server-saved and newly added)
        const toPayload = (arr: CustomDocItem[], type: CustomDocumentPayload['type']) =>
            arr
                .filter(d => d.title.trim() && d.file)
                .map(d => ({
                    title: d.title.trim(),
                    file: d.file,
                    type,
                    keyName: `${d.title.trim()}-other`,
                }))
        return [
            ...toPayload(personalCustomDocs, 'PERSONAL'),
            ...toPayload(educationCustomDocs, 'EDUCATION'),
            ...toPayload(otherDocs, 'OTHER'),
        ]
    }

    const renderFileField = (
        name: string,
        label: string,
        placeholder: string,
        error?: string,
        required: boolean = false,
    ) => {
        const value = watch(name) as string | undefined

        const handleRemoveClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            const docId = docIds[name]
            setDocToDelete({ name, label, docId })
            setIsDeleteModalOpen(true)
        }

        const isImage = value && value.toLowerCase().match(/\.(jpg|jpeg|png|webp|gif|svg)$/)
        const fileName = value ? value.split('/').pop() : ''

        return (
            <div className="flex flex-col gap-1.5">
                {value ? (
                    <div className="group relative flex items-center gap-3.5 p-3.5 bg-gradient-to-r from-emerald-50/70 via-emerald-50/30 to-white border border-emerald-200/90 rounded-2xl hover:shadow-sm transition-all">
                        {isImage ? (
                            <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-emerald-200 shrink-0 bg-white shadow-sm">
                                <img
                                    src={`${API_URL}/${value}`}
                                    alt={label}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                            </div>
                        ) : (
                            <div className="w-12 h-12 rounded-xl border border-emerald-200 bg-emerald-50/80 flex flex-col items-center justify-center shrink-0 shadow-sm">
                                <FileText className="w-5 h-5 text-emerald-600" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mt-0.5">
                                    {value.split('.').pop() || 'FILE'}
                                </span>
                            </div>
                        )}
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-slate-800 truncate">{label}</span>
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full shrink-0">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Uploaded
                                </span>
                            </div>
                            <p className="text-[11px] text-slate-500 font-medium truncate max-w-[200px]" title={fileName}>
                                {fileName}
                            </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <button
                                type="button"
                                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors"
                                title="View / Preview Document"
                                onClick={e => {
                                    e.stopPropagation()
                                    setPreviewDoc({ url: value, title: label || fileName || "Document" })
                                }}
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors"
                                title="Download Document"
                                onClick={e => {
                                    e.stopPropagation()
                                    handleDownload(value, fileName || label)
                                }}
                            >
                                <Download className="w-4 h-4" />
                            </button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={isDeleting}
                                onClick={handleRemoveClick}
                                className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove document"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="relative group">
                        <label className="relative flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 hover:border-primary/60 bg-slate-50/40 hover:bg-primary/[0.02] rounded-2xl cursor-pointer transition-all duration-200 text-center">
                            <input
                                type="file"
                                onChange={handleFileChange(name)}
                                className="hidden"
                                accept={ALLOWED_PHOTO_PDF_ACCEPT}
                            />
                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-200 mb-2">
                                <UploadCloud className="w-5 h-5" />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-semibold text-slate-700 group-hover:text-primary transition-colors">
                                    {label}
                                </span>
                                {required && <span className="text-red-500 text-xs font-bold">*</span>}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                {placeholder || 'Click or drag file to upload'}
                            </p>
                        </label>
                        {error && (
                            <p className="text-[11px] text-red-500 font-medium mt-1 pl-1">{error}</p>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // ── Render: single custom doc row ──────────────────────────────────────────────────
    const renderCustomDocRow = (section: Section, doc: CustomDocItem) => {
        const isNameEmpty = !doc.title.trim()
        const isImage = doc.file && doc.file.toLowerCase().match(/\.(jpg|jpeg|png|webp|gif|svg)$/)

        return (
            <div
                key={doc.id}
                className="p-4 border border-slate-200/90 rounded-2xl bg-slate-50/40 flex flex-col md:flex-row md:items-end gap-4"
            >
                {/* Document Name */}
                <div className="flex-1">
                    <FormField
                        type="text"
                        label="Document Name"
                        placeholder="e.g. Experience Certificate"
                        value={doc.title}
                        onChange={e =>
                            handleCustomDocTitleChange(section, doc.id, (e.target as HTMLInputElement).value)
                        }
                    />
                </div>

                {/* File area */}
                <div className="flex-1">
                    {doc.file ? (
                        // ── Uploaded: same green card style as fixed docs ────────────────────
                        <div className="group relative flex items-center gap-3.5 p-3.5 bg-gradient-to-r from-emerald-50/70 via-emerald-50/30 to-white border border-emerald-200/90 rounded-2xl hover:shadow-sm transition-all">
                            {isImage ? (
                                <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-emerald-200 shrink-0 bg-white shadow-sm">
                                    <img
                                        src={`${API_URL}/${doc.file}`}
                                        alt={doc.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                </div>
                            ) : (
                                <div className="w-12 h-12 rounded-xl border border-emerald-200 bg-emerald-50/80 flex flex-col items-center justify-center shrink-0 shadow-sm">
                                    <FileText className="w-5 h-5 text-emerald-600" />
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mt-0.5">
                                        {doc.file.split('.').pop() || 'FILE'}
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-bold text-slate-800 truncate">{doc.title || 'Document'}</span>
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full shrink-0">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Uploaded
                                    </span>
                                </div>
                                <p className="text-[11px] text-slate-500 font-medium truncate max-w-[180px]" title={doc.file.split('/').pop()}>
                                    {doc.file.split('/').pop()}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors"
                                    title="View / Preview Document"
                                    onClick={e => {
                                        e.stopPropagation()
                                        setPreviewDoc({ url: doc.file, title: doc.title || 'Custom Document' })
                                    }}
                                >
                                    <Eye className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors"
                                    title="Download Document"
                                    onClick={e => {
                                        e.stopPropagation()
                                        handleDownload(doc.file, doc.title || doc.file.split('/').pop())
                                    }}
                                >
                                    <Download className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        // ── Not uploaded: disabled until Document Name is filled ──────────────
                        <div title={isNameEmpty ? 'Please enter Document Name first' : undefined}>
                            <label
                                className={`flex flex-col items-center justify-center p-3 border-2 border-dashed rounded-xl text-center transition-all duration-200 ${isNameEmpty
                                    ? 'border-slate-200 bg-slate-100/60 opacity-50 cursor-not-allowed pointer-events-none'
                                    : 'border-slate-200 hover:border-primary/60 bg-slate-50/40 hover:bg-primary/[0.02] cursor-pointer'
                                    }`}
                            >
                                <input
                                    type="file"
                                    onChange={handleCustomDocFileChange(section, doc.id)}
                                    className="hidden"
                                    accept={ALLOWED_PHOTO_PDF_ACCEPT}
                                    disabled={isNameEmpty}
                                />
                                <div className="flex items-center gap-2">
                                    {doc.isUploading ? (
                                        <Spinner />
                                    ) : (
                                        <UploadCloud className={`w-4 h-4 ${isNameEmpty ? 'text-slate-300' : 'text-slate-400'}`} />
                                    )}
                                    <span className={`text-xs font-medium ${isNameEmpty ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {doc.isUploading ? 'Uploading...' : isNameEmpty ? 'Enter name first' : 'Choose File'}
                                    </span>
                                </div>
                            </label>
                        </div>
                    )}
                </div>

                {/* Remove row button */}
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveCustomDoc(section, doc.id)}
                    className="self-end md:self-center h-9 w-9 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl shrink-0"
                    title="Remove this document"
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>
        )
    }

    // â”€â”€ Render: "Add Custom Document" block per section 
    const renderCustomDocSection = (
        section: Section,
        docs: CustomDocItem[],
        descriptionText: string,
    ) => (
        <div className="space-y-3 mt-4 pt-4 border-t border-slate-100 ">
            <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500 font-medium">{descriptionText}</p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddCustomDoc(section)}
                    className="h-9 gap-1.5 border-dashed border-primary/50 text-primary hover:bg-primary/5 rounded-xl font-semibold shrink-0"
                >
                    <Plus className="w-4 h-4" /> Add Custom Document
                </Button>
            </div>

            {docs.length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-2xl p-5 text-center bg-slate-50/40">
                    <FileText className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-slate-500">No custom documents added yet</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                        Click "+ Add Custom Document" to attach additional files.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {docs.map(doc => renderCustomDocRow(section, doc))}
                </div>
            )}
        </div>
    )

    // ── Required field validation checks ────────────────────────────────────────
    const isPersonalMissing = !watch('aadharFront') || !watch('panFront') || (isMarried && !watch('marriageCertificate')) || !watch('drivingLicense') || !watch('currentAddressProof') || !watch('permanentAddressProof')
    const isEducationMissing = !watch('sscDoc')

    const personalHasError = hasSubmitted && (isPersonalMissing || !!errors.aadharFront || !!errors.panFront)
    const educationHasError = hasSubmitted && (isEducationMissing || !!errors.sscDoc)

    // ── Save & Continue ────────────────────────────────────────────────────────
    const handleSave = () => {
        setHasSubmitted(true)
        let hasError = false
        const sectionsToOpen: string[] = []

        // 1. Validate required PERSONAL fields
        if (!watch('aadharFront')) {
            methods.setError('aadharFront', { type: 'manual', message: 'Aadhar Card Front is required' })
            hasError = true
            sectionsToOpen.push('personal-docs')
        } else {
            methods.clearErrors('aadharFront')
        }

        if (!watch('panFront')) {
            methods.setError('panFront', { type: 'manual', message: 'PAN Card is required' })
            hasError = true
            if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        } else {
            methods.clearErrors('panFront')
        }

        if (isMarried) {
            if (!watch('marriageCertificate')) {
                methods.setError('marriageCertificate', { type: 'manual', message: 'Marriage Certificate is required' })
                hasError = true
                if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
            } else {
                methods.clearErrors('marriageCertificate')
            }
        }
        if (!watch('drivingLicense')) {
            methods.setError('drivingLicense', { type: 'manual', message: 'Driving License is required' })
            hasError = true
            if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        } else {
            methods.clearErrors('drivingLicense')
        }
        if (!watch('currentAddressProof')) {
            methods.setError('currentAddressProof', { type: 'manual', message: 'Current Address Proof is required' })
            hasError = true
            if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        } else {
            methods.clearErrors('currentAddressProof')
        }
        if (!watch('permanentAddressProof')) {
            methods.setError('permanentAddressProof', { type: 'manual', message: 'Permanent Address Proof is required' })
            hasError = true
            if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        } else {
            methods.clearErrors('permanentAddressProof')
        }
        // if(!watch('passport')){
        //     methods.setError('passport', { type: 'manual', message: 'Passport is required' })
        //     hasError = true
        //     if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        // }else{
        //     methods.clearErrors('passport')
        // }


        // 2. Validate required EDUCATION fields
        if (!watch('sscDoc')) {
            methods.setError('sscDoc', { type: 'manual', message: 'SSC Marksheet is required' })
            hasError = true
            if (!sectionsToOpen.includes('education-docs')) sectionsToOpen.push('education-docs')
        } else {
            methods.clearErrors('sscDoc')
        }

        // 3. Validate incomplete custom documents (title entered but no file uploaded)
        const incompletePersonal = personalCustomDocs.some(d => d.title.trim() && !d.file)
        const incompleteEducation = educationCustomDocs.some(d => d.title.trim() && !d.file)
        const incompleteOther = otherDocs.some(d => d.title.trim() && !d.file)

        if (incompletePersonal) {
            hasError = true
            if (!sectionsToOpen.includes('personal-docs')) sectionsToOpen.push('personal-docs')
        }
        if (incompleteEducation) {
            hasError = true
            if (!sectionsToOpen.includes('education-docs')) sectionsToOpen.push('education-docs')
        }
        if (incompleteOther) {
            hasError = true
            if (!sectionsToOpen.includes('other-docs')) sectionsToOpen.push('other-docs')
        }

        if (hasError) {
            setOpenSections(prev => Array.from(new Set([...prev, ...sectionsToOpen])))
            showToastNotification("danger", 'Please upload all required documents')
            return
        }

        const customDocs = buildCustomDocPayload()
        // Build fields list — include marriageCertificate only when user is married
        const docFields = [
            'aadharFront', 'aadharBack', 'panFront', 'drivingLicense', 'passport', 'currentAddressProof', 'permanentAddressProof',
            ...(isMarried ? ['marriageCertificate'] : []),
            'sscDoc', 'hscDoc', 'graduationDoc', 'postGraduationDoc', 'mastersDoc',
        ]

        handleNextStep(
            'documents',
            'tab9',
            docFields as (keyof EmployeeOnBoard)[],
            customDocs,
        )
    }

    // ── JSX ──────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            <Accordion
                type="multiple"
                value={openSections}
                onValueChange={setOpenSections}
                className="w-full space-y-4"
            >

                {/* ── 1. Personal Identity Documents ─────────────────────── */}
                <AccordionItem
                    value="personal-docs"
                    className={`border bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-200 ${personalHasError
                        ? 'border-red-300 ring-2 ring-red-100 bg-red-50/10'
                        : 'border-slate-200/90 hover:border-slate-300'
                        }`}
                >
                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50/70 transition-colors group">
                        <div className="flex items-center gap-3.5 text-left flex-1 min-w-0 mr-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100/80 group-hover:scale-105 transition-transform">
                                <UserCheck className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="font-bold text-slate-800 text-sm md:text-base">
                                    Personal Identity Documents
                                </span>
                                <span className="text-xs text-slate-500 truncate">
                                    Aadhar Card, PAN Card, Driving License, Passport, Current Address Proof, Permanent Address Proof{isMarried && ", Marriage Certificate"}
                                </span>
                            </div>
                        </div>
                        <div className="mr-3 shrink-0">
                            {personalHasError ? (
                                <Badge className="bg-red-50 text-red-600 border border-red-200/90 gap-1 font-semibold px-2.5 py-1 shadow-sm">
                                    <AlertCircle className="w-3.5 h-3.5 text-red-500" /> Action Required
                                </Badge>
                            ) : personalUploaded === PERSONAL_FIELDS.length ? (
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 gap-1 font-semibold px-2.5 py-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="bg-slate-100/80 text-slate-600 border-slate-200/80 gap-1 font-medium px-2.5 py-1">
                                    {personalUploaded} / {PERSONAL_FIELDS.length} Uploaded
                                </Badge>
                            )}
                        </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-5 pb-5 pt-2 h-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                            {renderFileField('aadharFront', 'Aadhar Card Front', 'Upload Aadhar front image/PDF', errors.aadharFront?.message as string, true)}
                            {renderFileField('panFront', 'PAN Card', 'Upload PAN Card copy', errors.panFront?.message as string, true)}
                            {renderFileField('aadharBack', 'Aadhar Card Back', 'Upload Aadhar back image/PDF', errors.aadharBack?.message as string)}
                            {renderFileField('currentAddressProof', 'Current Address Proof', 'Upload current address proof', errors.currentAddressProof?.message as string, true)}
                            {renderFileField('permanentAddressProof', 'Permanent Address Proof', 'Upload permanent address proof', errors.permanentAddressProof?.message as string, true)}
                            {isMarried && renderFileField('marriageCertificate', 'Marriage Certificate', 'Upload Marriage Certificate', errors.marriageCertificate?.message as string, true)}
                            {renderFileField('drivingLicense', 'Driving License', 'Upload driving license copy', errors.drivingLicense?.message as string, true)}
                            {renderFileField('passport', 'Passport', 'Upload Passport copy', errors.passport?.message as string)}
                        </div>
                        {renderCustomDocSection(
                            'personal',
                            personalCustomDocs,
                            'Add extra personal identity documents (e.g. Voter ID, Ration Card).',
                        )}
                    </AccordionContent>
                </AccordionItem>

                {/* ── 2. Educational & Academic Documents ───────────────── */}
                <AccordionItem
                    value="education-docs"
                    className={`border bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-200 ${educationHasError
                        ? 'border-red-300 ring-2 ring-red-100 bg-red-50/10'
                        : 'border-slate-200/90 hover:border-slate-300'
                        }`}
                >
                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50/70 transition-colors group">
                        <div className="flex items-center gap-3.5 text-left flex-1 min-w-0 mr-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100/80 group-hover:scale-105 transition-transform">
                                <GraduationCap className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="font-bold text-slate-800 text-sm md:text-base">
                                    Educational & Academic Documents
                                </span>
                                <span className="text-xs text-slate-500 truncate">
                                    SSC, HSC, Graduation, Post Graduation, Masters & Qualifications
                                </span>
                            </div>
                        </div>
                        <div className="mr-3 shrink-0">
                            {educationHasError ? (
                                <Badge className="bg-red-50 text-red-600 border border-red-200/90 gap-1 font-semibold px-2.5 py-1 shadow-sm">
                                    <AlertCircle className="w-3.5 h-3.5 text-red-500" /> Action Required
                                </Badge>
                            ) : educationUploaded === EDUCATION_FIELDS.length ? (
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 gap-1 font-semibold px-2.5 py-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="bg-slate-100/80 text-slate-600 border-slate-200/80 gap-1 font-medium px-2.5 py-1">
                                    {educationUploaded} / {EDUCATION_FIELDS.length} Uploaded
                                </Badge>
                            )}
                        </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-5 pb-5 pt-2 h-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                            {renderFileField('sscDoc', 'SSC Marksheet', 'Upload SSC marksheet/certificate', errors.sscDoc?.message as string, true)}
                            {renderFileField('hscDoc', 'HSC Marksheet', 'Upload HSC marksheet/certificate', errors.hscDoc?.message as string)}
                            {renderFileField('graduationDoc', 'Graduation', 'Upload Graduation degree/marksheet', errors.graduationDoc?.message as string)}
                            {/* {renderFileField('postGraduationDoc', 'Post Graduation', 'Upload Post Graduation degree/marksheet', errors.postGraduationDoc?.message as string)} */}
                            {renderFileField('mastersDoc', 'Masters', 'Upload Masters degree/marksheet', errors.mastersDoc?.message as string)}
                        </div>
                        {renderCustomDocSection(
                            'education',
                            educationCustomDocs,
                            'Add extra academic documents (e.g. Diploma, Certifications).',
                        )}
                    </AccordionContent>
                </AccordionItem>

                {/* â”€â”€ 3. Other & Supporting Documents â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
                <AccordionItem
                    value="other-docs"
                    className="border border-slate-200/90 bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-200 hover:border-slate-300"
                >
                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50/70 transition-colors group">
                        <div className="flex items-center gap-3.5 text-left flex-1 min-w-0 mr-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0 border border-amber-100/80 group-hover:scale-105 transition-transform">
                                <FolderKanban className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="font-bold text-slate-800 text-sm md:text-base">
                                    Other & Supporting Documents
                                </span>
                                <span className="text-xs text-slate-500 truncate">
                                    Experience certificates, Relieving letters, or any other documents
                                </span>
                            </div>
                        </div>
                        <div className="mr-3 shrink-0">
                            <Badge
                                variant="outline"
                                className="bg-amber-50/80 text-amber-700 border-amber-200/80 font-medium px-2.5 py-1"
                            >
                                Optional ({otherDocs.filter(d => d.file).length}/{otherDocs.length})
                            </Badge>
                        </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-5 pb-5 pt-2 h-auto">
                        {/* No fixed docs â€” only custom docs for "Other" */}
                        {renderCustomDocSection(
                            'other',
                            otherDocs,
                            'Upload any additional documents (e.g. Relieving Letter, Salary Slips, Offer Letter).',
                        )}
                    </AccordionContent>
                </AccordionItem>

            </Accordion>

            {/* â”€â”€ Save & Continue â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="flex justify-end mt-6">
                <Button
                    type="button"
                    onClick={handleSave}
                    disabled={isParentLoading || isDeleting}
                    className="px-6 rounded-xl font-semibold shadow-sm"
                >
                    {isParentLoading ? <Spinner /> : 'Save and Continue'}
                </Button>
            </div>

            {/* â”€â”€ Delete Confirmation Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <CommonModal
                show={isDeleteModalOpen}
                onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
                title=""
                className="max-w-sm rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                            <Trash2 size={22} className="text-red-500" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1.5 font-heading">
                            Delete Document
                        </h3>
                        <p className="text-sm text-gray-500 leading-relaxed mb-6">
                            Are you sure you want to delete{' '}
                            <strong>{docToDelete?.label}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex items-center gap-3 w-full">
                            <Button
                                variant="outline"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                                className="flex-1 rounded-xl h-11"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                className="flex-1 rounded-xl h-11 flex items-center justify-center gap-2"
                            >
                                {isDeleting ? <Spinner /> : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            </CommonModal>

            {/* ── File In-App Preview Modal (PDF / Image) ── */}
            <FilePreviewModal
                isOpen={Boolean(previewDoc)}
                onClose={() => setPreviewDoc(null)}
                fileUrl={previewDoc?.url || null}
                title={previewDoc?.title || "Document Preview"}
            />
        </div>
    )
}

export default OnboardDocuments
